import React, { useState } from "react";
import { MessageCircle, X, Send, HelpCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { L } from "@/constants/labels";

/**
 * Assistant contextuel — bulle flottante en bas à droite.
 * Portée : uniquement les pages authentifiées (monté depuis Layout).
 * Réponses : uniquement issues d'une base rédigée par l'admin (matching mots-clés).
 * AUCUNE génération libre : si aucune entrée ne matche, message de repli standard.
 */
export default function AssistantWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null); // { answer, matched, log_id, question_source }
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [feedbackBusy, setFeedbackBusy] = useState(false);

  if (!user) return null; // Widget uniquement pour utilisateurs connectés

  const ask = async (e) => {
    e?.preventDefault();
    if (!question.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/ask", { question });
      setAnswer(data);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const sendFeedback = async (e) => {
    e.preventDefault();
    if (feedbackMsg.trim().length < 2) return;
    setFeedbackBusy(true);
    try {
      await api.post("/assistant/feedback", {
        log_id: answer?.log_id || null,
        question,
        message: feedbackMsg,
      });
      toast.success("Message envoyé. L'équipe VosCompagnons vous répondra rapidement.");
      setShowFeedback(false);
      setFeedbackMsg("");
      setAnswer(null);
      setQuestion("");
    } catch (err) { toast.error(fmtError(err)); }
    finally { setFeedbackBusy(false); }
  };

  const reset = () => {
    setQuestion(""); setAnswer(null); setShowFeedback(false); setFeedbackMsg("");
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="assistant-toggle"
          aria-label="Ouvrir l'assistant"
          className="fixed z-40 bottom-24 md:bottom-6 right-4 md:right-6 h-12 w-12 rounded-full bg-[#1B3A5C] hover:bg-[#132a44] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        >
          <MessageCircle size={22} />
        </button>
      )}

      {open && (
        <div
          className="fixed z-40 bottom-24 md:bottom-6 right-4 md:right-6 w-[calc(100vw-2rem)] md:w-[380px] bg-white border border-[#E4E7EB] rounded-[8px] shadow-2xl flex flex-col overflow-hidden"
          style={{ maxHeight: "70vh" }}
          data-testid="assistant-panel"
        >
          <header className="flex items-center justify-between p-3 border-b border-[#E4E7EB] bg-[#1B3A5C] text-white">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <HelpCircle size={16} /> Assistant VosCompagnons
            </div>
            <button
              onClick={() => { setOpen(false); reset(); }}
              data-testid="assistant-close"
              aria-label="Fermer"
              className="hover:opacity-80"
            >
              <X size={18} />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {!answer && !showFeedback && (
              <>
                <p className="text-xs text-[#3E4C59]">
                  {L.wording.assistantIntro}
                  Pour toute autre demande, écrivez à <span className="font-mono">contact@voscompagnons.fr</span>.
                </p>
                <form onSubmit={ask} className="space-y-2">
                  <textarea
                    className="input min-h-[80px] resize-none"
                    placeholder="Posez votre question…"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    maxLength={500}
                    data-testid="assistant-input"
                  />
                  <button
                    type="submit"
                    disabled={busy || !question.trim()}
                    className="btn-primary w-full"
                    data-testid="assistant-send"
                  >
                    <Send size={14} /> {busy ? "…" : "Envoyer"}
                  </button>
                </form>
              </>
            )}

            {answer && !showFeedback && (
              <div className="space-y-3" data-testid="assistant-answer-block">
                <div className="text-xs uppercase tracking-wider text-[#3E4C59]">Votre question</div>
                <div className="text-sm text-[#1F2933] italic">« {question} »</div>
                {answer.question_source && (
                  <div className="text-xs text-[#3E4C59]">
                    Question la plus proche : <span className="font-semibold text-[#1B3A5C]">{answer.question_source}</span>
                  </div>
                )}
                <div
                  className="text-sm text-[#1F2933] whitespace-pre-wrap border-l-4 border-[#1B3A5C] pl-3 py-1"
                  data-testid="assistant-answer"
                >
                  {answer.answer}
                </div>
                <div className="flex flex-col gap-2 pt-1">
                  <button
                    onClick={() => setShowFeedback(true)}
                    className="btn-outline text-xs"
                    data-testid="assistant-not-what-i-need"
                  >
                    Ce n'est pas ce que je cherchais
                  </button>
                  <button
                    onClick={reset}
                    className="btn-ghost text-xs"
                    data-testid="assistant-new-question"
                  >
                    Poser une autre question
                  </button>
                </div>
              </div>
            )}

            {showFeedback && (
              <form onSubmit={sendFeedback} className="space-y-2" data-testid="assistant-feedback-form">
                <div className="text-sm font-semibold text-[#1F2933]">Écrivez-nous</div>
                <p className="text-xs text-[#3E4C59]">
                  L'équipe vous répondra à <span className="font-mono">{user.email}</span>.
                </p>
                <textarea
                  className="input min-h-[100px] resize-none"
                  placeholder="Décrivez précisément ce que vous cherchez…"
                  value={feedbackMsg}
                  onChange={(e) => setFeedbackMsg(e.target.value)}
                  maxLength={2000}
                  data-testid="assistant-feedback-input"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowFeedback(false)}
                    className="btn-outline text-xs flex-1"
                    data-testid="assistant-feedback-cancel"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={feedbackBusy || feedbackMsg.trim().length < 2}
                    className="btn-primary text-xs flex-1"
                    data-testid="assistant-feedback-submit"
                  >
                    <Mail size={14} /> {feedbackBusy ? "…" : "Envoyer"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
